import { FloatingPaneMenuItem } from '../FloatingPane';
import { usePortalContainer } from '../PortalContainerContext';
import { closeMenu, navigate, openMenu } from './menu-actions';
import type { NavigateDirection } from './menu-actions';
import { MenuProvider, MenuRadioGroupProvider, useMenuContext, useMenuRadioGroupContext } from './menu-context';
import { menuReducer } from './menu-reducer';
import { initialMenuState } from './menu-state';
import { getNavigableItems, useMenuItemInteraction } from './menu-utils';
import {
    autoUpdate,
    flip,
    FloatingNode,
    FloatingPortal,
    FloatingTree,
    offset as offsetMiddleware,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFloatingParentNodeId,
    useHover,
    useInteractions,
    useMergeRefs,
    useRole,
    useTransitionStatus,
} from '@floating-ui/react';
import type { OpenChangeReason, Placement } from '@floating-ui/react';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';
import { faCircle } from '@fortawesome/pro-solid-svg-icons/faCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@thinkalpha/ui-core/utils';
import { useCallback, useEffect, useReducer, useRef } from 'react';

export const MENU_CONTENT_PADDING = 4;
const MENU_CONTENT_PADDING_CLASS = 'p-[4px]';
const FADE_DURATION_MS = 100;

/**
 * Menu-specific floating panel. Uses a simple CSS opacity transition instead
 * of FloatingPane's keyframe-based animate-in/animate-out system.
 *
 * FloatingPane's keyframe animations fight with floating-ui positioning:
 * on the first render frame, floatingStyles is at (0,0) before the position
 * is computed — the keyframe animation begins from that wrong origin, causing
 * a visible slide from the top-left corner of the viewport. By using an
 * opacity transition instead, the panel is invisible on the initial frame
 * (while floating-ui computes position) and fades in once positioned.
 */
interface MenuPanelProps extends React.HTMLAttributes<HTMLDivElement> {
    /** Transition status from useTransitionStatus ('initial' | 'open' | 'close') */
    status: string;
    ref?: React.Ref<HTMLDivElement>;
}

const MenuPanel = ({ children, className, status, style, ref, ...props }: MenuPanelProps) => {
    return (
        <div
            ref={ref}
            {...props}
            className={cn(
                'bg-gray-50 text-gray-925 dark:bg-gray-925 dark:text-gray-50',
                'z-50 overflow-hidden rounded-lg border border-gray-700 shadow-md',
                className,
            )}
            style={{
                ...style,
                opacity: status === 'open' ? 1 : 0,
                transition: `opacity ${FADE_DURATION_MS}ms ease-out`,
            }}
        >
            {children}
        </div>
    );
};

interface MenuProps {
    children: React.ReactNode;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (details: { open: boolean }) => void;
    /** Placement of the floating content relative to the trigger. Default: 'bottom-start' */
    placement?: Placement;
    /** Offset distance in px from the trigger. Default: 4 */
    offset?: number;
}

/**
 * Internal component that sets up floating-ui state and provides context.
 * Wrapped by `Menu` which adds `FloatingTree` at the root level.
 */
const MenuInner = ({ children, open: controlledOpen, defaultOpen, onOpenChange, placement, offset }: MenuProps) => {
    const [state, dispatch] = useReducer(menuReducer, {
        ...initialMenuState,
        open: defaultOpen ?? false,
    });
    const contentRef = useRef<HTMLDivElement>(null);

    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : state.open;

    const setOpen = useCallback(
        (nextOpen: boolean) => {
            dispatch(nextOpen ? openMenu() : closeMenu());
            onOpenChange?.({ open: nextOpen });
        },
        [onOpenChange],
    );

    // Sync highlight reset when controlled open changes
    useEffect(() => {
        if (isControlled) {
            dispatch(controlledOpen ? openMenu() : closeMenu());
        }
    }, [isControlled, controlledOpen]);

    const navigateItems = useCallback((direction: NavigateDirection) => {
        const items = getNavigableItems(contentRef.current);
        dispatch(navigate(direction, items.length));
    }, []);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: placement ?? 'bottom-start',
        middleware: [offsetMiddleware(offset ?? 4), flip(), shift()],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    const close = useCallback(() => setOpen(false), [setOpen]);

    return (
        <MenuProvider
            value={{
                open,
                close,
                setReference: refs.setReference,
                setFloating: refs.setFloating,
                getTriggerProps: getReferenceProps,
                getFloatingProps: getFloatingProps,
                floatingStyles,
                floatingContext: context,
                highlightedIndex: state.highlightedIndex,
                dispatch,
                contentRef,
                navigateItems,
            }}
        >
            {children}
        </MenuProvider>
    );
};

const Menu = (props: MenuProps) => {
    const parentId = useFloatingParentNodeId();

    // Only wrap in FloatingTree at the root level (not inside submenus)
    if (parentId === null) {
        return (
            <FloatingTree>
                <MenuInner {...props} />
            </FloatingTree>
        );
    }
    return <MenuInner {...props} />;
};

interface MenuTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
    asChild?: boolean;
    ref?: React.Ref<HTMLButtonElement>;
}

const MenuTrigger = ({ asChild, children, ref, ...props }: MenuTriggerProps) => {
    const { setReference, getTriggerProps } = useMenuContext();
    const mergedRef = useMergeRefs([ref, setReference]);
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp ref={mergedRef} {...getTriggerProps(props)}>
            {children}
        </Comp>
    );
};

interface MenuContentProps {
    children: React.ReactNode;
    className?: string;
    container?: HTMLElement | null;
    ref?: React.Ref<HTMLDivElement>;
}

const MenuContent = ({ children, className, container, ref }: MenuContentProps) => {
    const {
        open,
        setFloating,
        getFloatingProps,
        floatingStyles,
        floatingContext,
        highlightedIndex,
        contentRef,
        navigateItems,
    } = useMenuContext();
    const contextContainer = usePortalContainer();
    const portalTarget = container ?? contextContainer ?? undefined;
    const { isMounted, status } = useTransitionStatus(floatingContext, { duration: FADE_DURATION_MS });

    const mergedRef = useMergeRefs([ref, setFloating, contentRef]);

    // Focus the content when the menu opens so keyboard events are captured
    useEffect(() => {
        if (open && status === 'open' && contentRef.current) {
            contentRef.current.focus();
        }
    }, [open, status, contentRef]);

    // Imperatively apply data-highlighted to the correct item and scroll it into view
    useEffect(() => {
        if (!contentRef.current) return;
        const items = getNavigableItems(contentRef.current);
        for (const item of items) {
            item.removeAttribute('data-highlighted');
        }
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
            items[highlightedIndex]!.setAttribute('data-highlighted', '');
            items[highlightedIndex]!.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex, contentRef]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    navigateItems('down');
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    navigateItems('up');
                    break;
                case 'ArrowRight':
                case 'ArrowLeft': {
                    const items = getNavigableItems(contentRef.current);
                    const item = items[highlightedIndex];
                    const submenuSide = item?.dataset.submenuSide;
                    if (submenuSide) {
                        const openKey = submenuSide === 'left' ? 'ArrowLeft' : 'ArrowRight';
                        if (e.key === openKey) {
                            e.preventDefault();
                            (item as any).__openSubmenu?.();
                        }
                    }
                    break;
                }
                case 'Home':
                    e.preventDefault();
                    navigateItems('first');
                    break;
                case 'End':
                    e.preventDefault();
                    navigateItems('last');
                    break;
                case 'Enter':
                case ' ': {
                    e.preventDefault();
                    const items = getNavigableItems(contentRef.current);
                    const item = items[highlightedIndex];
                    if (item?.dataset.submenuSide) {
                        (item as any).__openSubmenu?.();
                    } else {
                        item?.click();
                    }
                    break;
                }
            }
        },
        [navigateItems, highlightedIndex, contentRef],
    );

    if (!isMounted) return null;

    return (
        <FloatingPortal root={portalTarget}>
            <MenuPanel
                ref={mergedRef}
                {...getFloatingProps()}
                style={floatingStyles}
                status={status}
                className={cn('min-w-32 outline-hidden', MENU_CONTENT_PADDING_CLASS, className)}
                tabIndex={-1}
                onKeyDown={handleKeyDown}
            >
                {children}
            </MenuPanel>
        </FloatingPortal>
    );
};

interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
    disabled?: boolean;
    /** Whether clicking this item closes the menu. Default: true */
    closeOnSelect?: boolean;
    inset?: boolean;
    asChild?: boolean;
    ref?: React.Ref<HTMLDivElement>;
}

const MenuItem = ({
    children,
    disabled,
    closeOnSelect = true,
    inset,
    asChild,
    className,
    onClick,
    ref,
    ...props
}: MenuItemProps) => {
    const { close } = useMenuContext();
    const { onMouseEnter, onMouseLeave } = useMenuItemInteraction(disabled);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return;
        onClick?.(e);
        if (closeOnSelect && !e.defaultPrevented) {
            close();
        }
    };

    return (
        <FloatingPaneMenuItem
            ref={ref}
            role="menuitem"
            data-disabled={disabled ? '' : undefined}
            aria-disabled={disabled || undefined}
            asChild={asChild}
            className={cn(inset && 'pl-8', className)}
            onClick={handleClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            {...props}
        >
            {children}
        </FloatingPaneMenuItem>
    );
};

interface MenuCheckboxItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
    checked: boolean;
    onCheckedChange?: (checked: boolean) => void;
    disabled?: boolean;
    /** Whether clicking this item closes the menu. Default: false */
    closeOnSelect?: boolean;
    ref?: React.Ref<HTMLDivElement>;
}

const MenuCheckboxItem = ({
    children,
    checked,
    onCheckedChange,
    disabled,
    closeOnSelect = false,
    className,
    onClick,
    ref,
    ...props
}: MenuCheckboxItemProps) => {
    const { close } = useMenuContext();
    const { onMouseEnter, onMouseLeave } = useMenuItemInteraction(disabled);

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return;
        onClick?.(e);
        if (!e.defaultPrevented) {
            onCheckedChange?.(!checked);
            if (closeOnSelect) {
                close();
            }
        }
    };

    return (
        <FloatingPaneMenuItem
            ref={ref}
            role="menuitemcheckbox"
            aria-checked={checked}
            data-disabled={disabled ? '' : undefined}
            aria-disabled={disabled || undefined}
            className={cn('pr-2 pl-8', className)}
            onClick={handleClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            {...props}
        >
            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                {checked && <FontAwesomeIcon icon={faCheck} />}
            </span>
            {children}
        </FloatingPaneMenuItem>
    );
};

interface MenuRadioGroupProps {
    children: React.ReactNode;
    name: string;
    value: string;
    onValueChange: (value: string) => void;
}

const MenuRadioGroup = ({ children, name, value, onValueChange }: MenuRadioGroupProps) => {
    return (
        <MenuRadioGroupProvider value={{ name, value, onValueChange }}>
            <div role="group" aria-label={name}>
                {children}
            </div>
        </MenuRadioGroupProvider>
    );
};

interface MenuRadioItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'value'> {
    value: string;
    disabled?: boolean;
    /** Whether clicking this item closes the menu. Default: false */
    closeOnSelect?: boolean;
    ref?: React.Ref<HTMLDivElement>;
}

const MenuRadioItem = ({
    children,
    value,
    disabled,
    closeOnSelect = false,
    className,
    onClick,
    ref,
    ...props
}: MenuRadioItemProps) => {
    const { close } = useMenuContext();
    const { onMouseEnter, onMouseLeave } = useMenuItemInteraction(disabled);
    const { value: groupValue, onValueChange } = useMenuRadioGroupContext();
    const checked = groupValue === value;

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (disabled) return;
        onClick?.(e);
        if (!e.defaultPrevented) {
            onValueChange(value);
            if (closeOnSelect) {
                close();
            }
        }
    };

    return (
        <FloatingPaneMenuItem
            ref={ref}
            role="menuitemradio"
            aria-checked={checked}
            data-disabled={disabled ? '' : undefined}
            aria-disabled={disabled || undefined}
            className={cn('pr-2 pl-8', className)}
            onClick={handleClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            {...props}
        >
            <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                {checked && <FontAwesomeIcon icon={faCircle} size="xs" />}
            </span>
            {children}
        </FloatingPaneMenuItem>
    );
};

interface MenuGroupProps {
    children: React.ReactNode;
    id?: string;
}

const MenuGroup = ({ children, id }: MenuGroupProps) => {
    return (
        <div role="group" id={id}>
            {children}
        </div>
    );
};

interface MenuLabelProps extends React.HTMLAttributes<HTMLDivElement> {
    htmlFor?: string;
    inset?: boolean;
    ref?: React.Ref<HTMLDivElement>;
}

const MenuLabel = ({ children, className, htmlFor, inset, ref, ...props }: MenuLabelProps) => {
    return (
        <div
            ref={ref}
            id={htmlFor}
            className={cn(
                'px-2 py-1.5 text-sm font-semibold text-gray-500 dark:text-gray-400',
                inset && 'pl-8',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
};

const MenuSeparator = ({ className }: { className?: string }) => {
    return <div role="separator" className={cn('-mx-1 my-1 h-px bg-gray-200 dark:bg-gray-700', className)} />;
};

interface MenuItemIconProps {
    children: React.ReactNode;
}

const MenuItemIcon = ({ children }: MenuItemIconProps) => {
    return (
        <div className="relative mr-2 flex h-4 w-4 items-center justify-center group-[:hover:not([data-disabled])]/floating-pane-menu-item:text-azure-500">
            {children}
        </div>
    );
};

const MenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
    return <span className={cn('ml-auto text-sm tracking-widest opacity-60', className)} {...props} />;
};

interface MenuSubProps {
    children: React.ReactNode;
    /** Placement of the submenu. Default: 'right-start' */
    placement?: Placement;
    /** Offset distance in px from the trigger. Default: -4 */
    offset?: number;
}

const MenuSub = ({ children, placement, offset }: MenuSubProps) => {
    const parent = useMenuContext();
    const nodeId = useFloatingNodeId();
    const [state, dispatch] = useReducer(menuReducer, initialMenuState);
    const contentRef = useRef<HTMLDivElement>(null);

    const open = state.open;

    const setOpen = useCallback((nextOpen: boolean) => {
        dispatch(nextOpen ? openMenu() : closeMenu());
    }, []);

    // Guard against hover/safe-polygon closes when user has focus inside the submenu
    // (e.g. typing in a search input). When flip() repositions the floating element,
    // safePolygon detects the cursor outside the new polygon and tries to close —
    // this prevents that while the user is actively interacting with the content.
    const onOpenChange = useCallback(
        (nextOpen: boolean, _event?: Event, reason?: OpenChangeReason) => {
            const activeElement = contentRef.current?.ownerDocument.activeElement;
            const hasFocusedDescendant =
                !!contentRef.current &&
                !!activeElement &&
                activeElement !== contentRef.current &&
                contentRef.current.contains(activeElement);

            if (!nextOpen && (reason === 'hover' || reason === 'safe-polygon') && hasFocusedDescendant) {
                return;
            }
            setOpen(nextOpen);
        },
        [setOpen],
    );

    const navigateItems = useCallback((direction: NavigateDirection) => {
        const items = getNavigableItems(contentRef.current);
        dispatch(navigate(direction, items.length));
    }, []);

    const { refs, floatingStyles, context } = useFloating({
        nodeId,
        open,
        onOpenChange,
        placement: placement ?? 'right-start',
        middleware: [offsetMiddleware(offset ?? -4), flip(), shift()],
        whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context, {
        handleClose: safePolygon(),
        delay: { open: 75 },
    });
    const dismiss = useDismiss(context, { bubbles: true });
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

    const close = useCallback(() => {
        setOpen(false);
        parent.close();
    }, [parent, setOpen]);

    return (
        <FloatingNode id={nodeId}>
            <MenuProvider
                value={{
                    open,
                    close,
                    setReference: refs.setReference,
                    setFloating: refs.setFloating,
                    getTriggerProps: getReferenceProps,
                    getFloatingProps: getFloatingProps,
                    floatingStyles,
                    floatingContext: context,
                    highlightedIndex: state.highlightedIndex,
                    dispatch,
                    contentRef,
                    navigateItems,
                }}
            >
                {children}
            </MenuProvider>
        </FloatingNode>
    );
};

interface MenuSubTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
    inset?: boolean;
    ref?: React.Ref<HTMLDivElement>;
}

const MenuSubTrigger = ({ children, className, inset, ref, ...props }: MenuSubTriggerProps) => {
    const { setReference, getTriggerProps, floatingContext } = useMenuContext();
    const triggerRef = useRef<HTMLElement>(null);

    // Keep a stable ref to the latest floatingContext so the DOM callback never goes stale
    const floatingContextRef = useRef(floatingContext);
    floatingContextRef.current = floatingContext;

    // Expose an __openSubmenu function on the DOM element so the parent menu's keyboard
    // handler can open this submenu without going through useClick (which fights useHover).
    const attachOpenCallback = useCallback((el: HTMLElement | null) => {
        if (el) {
            (el as any).__openSubmenu = () => floatingContextRef.current.onOpenChange(true);
        }
    }, []);

    const mergedRef = useMergeRefs([ref, setReference, triggerRef, attachOpenCallback]);

    // Note: useMenuItemInteraction is intentionally NOT used here.
    // MenuSubTrigger lives inside MenuSub's provider (submenu context),
    // but visually belongs to the parent menu. Dispatching here would target
    // the wrong context. The trigger still gets hover styling from CSS and
    // keyboard highlighting from the parent's imperative highlight effect.

    const side = floatingContext.placement.startsWith('left') ? 'left' : 'right';

    return (
        <FloatingPaneMenuItem
            ref={mergedRef}
            role="menuitem"
            aria-haspopup="menu"
            data-submenu-side={side}
            {...getTriggerProps()}
            className={cn(inset && 'pl-8', className)}
            {...props}
        >
            {children}
            <div className="relative ml-auto flex h-4 w-4 items-center justify-center">
                <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
            </div>
        </FloatingPaneMenuItem>
    );
};

interface MenuSubContentProps {
    children: React.ReactNode;
    className?: string;
    container?: HTMLElement | null;
    ref?: React.Ref<HTMLDivElement>;
}

const MenuSubContent = ({ children, className, container, ref }: MenuSubContentProps) => {
    const {
        open,
        setFloating,
        getFloatingProps,
        floatingStyles,
        floatingContext,
        highlightedIndex,
        contentRef,
        navigateItems,
    } = useMenuContext();
    const contextContainer = usePortalContainer();
    const portalTarget = container ?? contextContainer ?? undefined;
    const { isMounted, status } = useTransitionStatus(floatingContext, { duration: FADE_DURATION_MS });

    const mergedRef = useMergeRefs([ref, setFloating, contentRef]);

    // Focus the submenu content when it opens
    useEffect(() => {
        if (open && status === 'open' && contentRef.current) {
            contentRef.current.focus();
        }
    }, [open, status, contentRef]);

    // Imperatively apply data-highlighted to the correct item
    useEffect(() => {
        if (!contentRef.current) return;
        const items = getNavigableItems(contentRef.current);
        for (const item of items) {
            item.removeAttribute('data-highlighted');
        }
        if (highlightedIndex >= 0 && highlightedIndex < items.length) {
            items[highlightedIndex]!.setAttribute('data-highlighted', '');
            items[highlightedIndex]!.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex, contentRef]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            // Determine which arrow key closes this submenu based on its resolved placement
            const side = floatingContext.placement.startsWith('left') ? 'left' : 'right';
            const closeKey = side === 'right' ? 'ArrowLeft' : 'ArrowRight';

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    e.stopPropagation();
                    navigateItems('down');
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    e.stopPropagation();
                    navigateItems('up');
                    break;
                case 'ArrowRight':
                case 'ArrowLeft': {
                    // First check: does this key open a nested submenu on the highlighted item?
                    const items = getNavigableItems(contentRef.current);
                    const item = items[highlightedIndex];
                    const submenuSide = item?.dataset.submenuSide;
                    if (submenuSide) {
                        const openKey = submenuSide === 'left' ? 'ArrowLeft' : 'ArrowRight';
                        if (e.key === openKey) {
                            e.preventDefault();
                            e.stopPropagation();
                            (item as any).__openSubmenu?.();
                            break;
                        }
                    }
                    // Second check: does this key close the current submenu?
                    if (e.key === closeKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        floatingContext.onOpenChange(false);
                        // Return focus to the parent menu content
                        (floatingContext.elements.reference as HTMLElement | null)
                            ?.closest<HTMLElement>('[role="menu"]')
                            ?.focus();
                    }
                    break;
                }
                case 'Home':
                    e.preventDefault();
                    e.stopPropagation();
                    navigateItems('first');
                    break;
                case 'End':
                    e.preventDefault();
                    e.stopPropagation();
                    navigateItems('last');
                    break;
                case 'Enter':
                case ' ': {
                    e.preventDefault();
                    e.stopPropagation();
                    const items = getNavigableItems(contentRef.current);
                    const item = items[highlightedIndex];
                    if (item?.dataset.submenuSide) {
                        (item as any).__openSubmenu?.();
                    } else {
                        item?.click();
                    }
                    break;
                }
            }
        },
        [navigateItems, highlightedIndex, contentRef, floatingContext],
    );

    if (!isMounted) return null;

    return (
        <FloatingPortal root={portalTarget}>
            <MenuPanel
                ref={mergedRef}
                {...getFloatingProps()}
                style={floatingStyles}
                status={status}
                className={cn('min-w-32 outline-hidden', MENU_CONTENT_PADDING_CLASS, className)}
                tabIndex={-1}
                onKeyDown={handleKeyDown}
            >
                {children}
            </MenuPanel>
        </FloatingPortal>
    );
};

export {
    Menu,
    MenuTrigger,
    MenuContent,
    MenuItem,
    MenuCheckboxItem,
    MenuRadioGroup,
    MenuRadioItem,
    MenuGroup,
    MenuLabel,
    MenuSeparator,
    MenuItemIcon,
    MenuShortcut,
    MenuSub,
    MenuSubTrigger,
    MenuSubContent,
};

export type {
    MenuProps,
    MenuTriggerProps,
    MenuContentProps,
    MenuItemProps,
    MenuCheckboxItemProps,
    MenuRadioGroupProps,
    MenuRadioItemProps,
    MenuGroupProps,
    MenuLabelProps,
    MenuSubProps,
    MenuSubTriggerProps,
    MenuSubContentProps,
    MenuItemIconProps,
    Placement as MenuPlacement,
};
